// @ts-check

/**
 * @typedef {Object} GridPreset
 * @property {string} label - Human-readable difficulty label.
 * @property {number} cols - Horizontal piece count for landscape artwork.
 * @property {number} rows - Vertical piece count for landscape artwork.
 * @property {number} [minRes] - Minimum image resolution (long side) required for this preset.
 * @property {boolean} [default] - Create Room selection and the stored-setting fallback.
 * @property {number} [botMinPlayers] - PuzzleBot uses this board at this player count and above. The highest matching band wins.
 */

/**
 * Difficulty presets, ascending by piece count.
 *
 * Shared by the lobby (player-facing buttons) and the server (PuzzleBot difficulty
 * selection), so it must stay free of DOM or Worker dependencies.
 *
 * @type {GridPreset[]}
 */
export const GRID_PRESETS = [
  { label: '9 x 5 (~50 pieces)', cols: 9, rows: 5, minRes: 1024 },
  { label: '14 x 8 (~100 pieces)', cols: 14, rows: 8, minRes: 1024, default: true, botMinPlayers: 0 },
  { label: '19 x 11 (~200 pieces)', cols: 19, rows: 11, minRes: 1280, botMinPlayers: 4 },
  { label: '27 x 15 (~400 pieces)', cols: 27, rows: 15, minRes: 1920, botMinPlayers: 6 },
  { label: '33 x 18 (~600 pieces)', cols: 33, rows: 18, minRes: 1920, botMinPlayers: 8 },
  { label: '38 x 21 (~800 pieces)', cols: 38, rows: 21, minRes: 2600 },
  { label: '43 x 24 (~1,000 pieces)', cols: 43, rows: 24, minRes: 2600 }
];

/** Largest standard board. A move or snap may name every piece, and no more. */
export const MAX_GRID_PIECES = GRID_PRESETS.reduce(
  (max, preset) => Math.max(max, preset.cols * preset.rows),
  0
);

/** Small boards shown only in Kids mode. */
export const KIDS_GRID_PRESETS = [
  { label: '5 x 3 (15 pieces)', cols: 5, rows: 3, minRes: 1024 },
  { label: '6 x 4 (24 pieces)', cols: 6, rows: 4, minRes: 1024 }
];

/** Kids Create Room hides any regular board size larger than this. */
export const KIDS_MAX_PIECES = 120;

/** Kids Create Room default among the Kids-only boards. */
export const DEFAULT_KIDS_GRID_PRESET =
  KIDS_GRID_PRESETS.find((preset) => preset.cols === 6 && preset.rows === 4) || KIDS_GRID_PRESETS[0];

/** Standard plus Kids boards, for lookup of an existing room grid. */
export const ALL_GRID_PRESETS = [...KIDS_GRID_PRESETS, ...GRID_PRESETS];

/**
 * Puzzle Size chips for Kids: Kids-only boards plus standard sizes up to {@link KIDS_MAX_PIECES}.
 *
 * @returns {GridPreset[]}
 */
export function kidsGridPresets() {
  return [
    ...KIDS_GRID_PRESETS,
    ...GRID_PRESETS.filter((preset) => preset.cols * preset.rows <= KIDS_MAX_PIECES)
  ];
}

/**
 * Largest Puzzle Size still offered in Kids mode.
 *
 * @returns {GridPreset}
 */
export function largestKidsGridPreset() {
  const list = kidsGridPresets();
  return list[list.length - 1] || DEFAULT_KIDS_GRID_PRESET;
}

/**
 * Default board. Mark exactly one preset with `default: true`.
 *
 * @type {GridPreset}
 */
export const DEFAULT_GRID_PRESET = GRID_PRESETS.find((preset) => preset.default) || GRID_PRESETS[0];

/**
 * Stored id for a preset, such as `14x8`. Settings, the lobby, and admin selects all use this.
 *
 * @param {{ cols: number, rows: number }} preset
 * @returns {string}
 */
export function gridPresetKey(preset) {
  return `${preset.cols}x${preset.rows}`;
}

/** Stored id of {@link DEFAULT_GRID_PRESET}. */
export const DEFAULT_GRID_PRESET_KEY = gridPresetKey(DEFAULT_GRID_PRESET);

/** Smallest standard board. Unknown house-room keys fall back here. */
export const SMALLEST_GRID_PRESET = GRID_PRESETS[0];

/** Stored id of {@link SMALLEST_GRID_PRESET}. */
export const SMALLEST_GRID_PRESET_KEY = gridPresetKey(SMALLEST_GRID_PRESET);

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isKnownGridPresetKey(value) {
  const key = String(value ?? '').trim();
  return GRID_PRESETS.some((preset) => gridPresetKey(preset) === key);
}

/**
 * Returns `value` when it names a current preset, otherwise `fallback`.
 *
 * @param {unknown} value
 * @param {string} [fallback]
 * @returns {string}
 */
export function resolveGridPresetKey(value, fallback = DEFAULT_GRID_PRESET_KEY) {
  const key = String(value ?? '').trim();
  return isKnownGridPresetKey(key) ? key : fallback;
}

/**
 * Option rows for an admin preset select. `auto` is PuzzleBot's player-count choice.
 *
 * @param {{ includeAuto?: boolean }} [options]
 * @returns {{ value: string, label: string }[]}
 */
export function gridPresetSelectOptions(options = {}) {
  const rows = GRID_PRESETS.map((preset) => ({
    value: gridPresetKey(preset),
    label: preset.label
  }));
  if (!options.includeAuto) return rows;
  return [{ value: 'auto', label: 'Auto (based on image size)' }, ...rows];
}

/**
 * PuzzleBot's board for a player count. Bands are the presets marked with `botMinPlayers`.
 *
 * @param {number} playerCount
 * @returns {GridPreset}
 */
export function piecePresetForPlayerCount(playerCount) {
  const raw = Number(playerCount);
  const count = Number.isFinite(raw)
    ? Math.max(0, Math.floor(raw))
    : (raw > 0 ? Number.POSITIVE_INFINITY : 0);
  const bands = GRID_PRESETS
    .filter((preset) => Number.isFinite(preset.botMinPlayers))
    .sort((a, b) => Number(b.botMinPlayers) - Number(a.botMinPlayers));
  return bands.find((preset) => count >= Number(preset.botMinPlayers)) || DEFAULT_GRID_PRESET;
}

/**
 * Finds the preset matching an exact grid, ignoring portrait orientation swaps.
 *
 * @param {number} cols
 * @param {number} rows
 * @returns {GridPreset|null} Matching preset, or null when the grid is custom.
 */
export function findGridPreset(cols, rows) {
  const c = Number(cols);
  const r = Number(rows);
  if (!Number.isFinite(c) || !Number.isFinite(r)) return null;
  return (
    ALL_GRID_PRESETS.find(
      (preset) =>
        (preset.cols === c && preset.rows === r) || (preset.cols === r && preset.rows === c)
    ) || null
  );
}

/**
 * Checks whether an image's long side satisfies a preset's minimum resolution.
 *
 * @param {GridPreset} preset
 * @param {number|null|undefined} longSide
 * @returns {boolean}
 */
export function isPresetAllowedForResolution(preset, longSide) {
  if (!preset || !Number.isFinite(longSide) || (longSide ?? 0) <= 0) return false;
  const min = preset.minRes ?? 0;
  return Number(longSide) >= min;
}

/** Piece count at which a create-room card used to be marked large. The 25×16 preset. */
export const LARGE_IMAGE_MIN_PIECES = 400;

/** Minimum long-side resolution for 4K / XL artwork classification. */
export const XL_IMAGE_MIN_LONG_SIDE = 3840;

/**
 * Create-room size letter from the largest preset the image can run, or 4K/XL dimension.
 *
 * @param {number|null|undefined} longSide
 * @returns {{ letter: 'S' | 'M' | 'L' | 'XL', title: string } | null}
 */
export function imageSizeMark(longSide) {
  const side = Number(longSide);
  if (Number.isFinite(side) && side >= XL_IMAGE_MIN_LONG_SIDE) {
    return { letter: 'XL', title: 'Extra Large' };
  }
  let max = 0;
  for (const preset of GRID_PRESETS) {
    const pieces = preset.cols * preset.rows;
    if (pieces > max && isPresetAllowedForResolution(preset, side)) max = pieces;
  }
  if (max >= 800) {
    return { letter: 'L', title: 'Large' };
  }
  if (max >= 400) {
    return { letter: 'M', title: 'Medium' };
  }
  if (max > 0) {
    return { letter: 'S', title: 'Small' };
  }
  return null;
}

/**
 * True when the image long side can run a preset of at least `minPieces`.
 * 400 pieces is 25×16 (long side 1920). 40×20 is 800 pieces and is included.
 *
 * @param {number|null|undefined} longSide
 * @param {number} [minPieces]
 * @returns {boolean}
 */
export function supportsPieceCount(longSide, minPieces = LARGE_IMAGE_MIN_PIECES) {
  const need = Number(minPieces);
  if (!Number.isFinite(need) || need <= 0) return false;
  return GRID_PRESETS.some(
    (preset) => preset.cols * preset.rows >= need && isPresetAllowedForResolution(preset, longSide)
  );
}
